import { useCallback, useContext, useMemo, useState } from 'react';
import clsx from 'clsx';
import { DragDropProvider } from '@dnd-kit/react';
import { useDefaultProject } from 'src/projects/hooks/useDefaultProject';
import { Pagination } from 'src/modules/common/components/Pagination/Pagination.tsx';
import { useProjectNavigation } from 'src/projects/navigation/ProjectNavigationContext.ts';
import { Button } from 'src/common/Button/Button';
import { RuntimeContext } from 'src/runtimeContext.ts';
import { Selection } from 'src/projects/types/Selection';
import { ConfigContext } from 'src/configContext';
import { CrossIcon } from 'src/modules/common/icons/CrossIcon';
import { I18n } from 'src/modules/common/components/I18n/I18n';
import { MESSAGES } from 'src/modules/common/constants';
import { ProjectEmptySelection } from 'src/projects/components/ProjectEmptySelection';
import { SkeletonSelectionRow } from 'src/projects/components/SkeletonSelectionRow.tsx';
import { ProjectSelectionItem } from 'src/projects/components/ProjectSelectionItem';
import { ExportExcelButton } from 'src/projects/components/ExportExcelButton';
import { AddProjectSelectionIcon } from 'src/modules/common/icons/AddProjectSelectionIcon';
import { useBelimoTranslate } from 'src/common/hooks/useBelimoTranslate';
import { CartIcon } from 'src/modules/common/icons/CartIcon';
import { prepareForCart } from 'src/projects/utils/prepareForCart';
import { fetchDesignData } from 'src/common/fetchDesignData.ts';
import { useApolloClient } from '@apollo/client';
import { addToCart } from 'src/utils/addToCart.ts';
import { CountTotalPrice } from 'src/projects/components/CountTotalPrice.tsx';
import { SelectionRepository } from 'src/projects/SelectionRepository.ts';
import { CheckIcon } from 'src/modules/common/icons/CheckIcon.tsx';
import { MinusIcon } from 'src/modules/common/icons/MinusIcon.tsx';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon.tsx';
import { SortableSelectionRow } from 'src/projects/components/SortableSelectionRow.tsx';
import { CrossPageDropZone } from 'src/projects/components/CrossPageDropZone.tsx';
import { useCrossPageDrag } from 'src/projects/hooks/useCrossPageDrag.ts';
import { confirm } from 'src/projects/utils/confirmation.tsx';

const limit = 10;

// Placeholder rows drawn while the selections are being read. The real count is known a few
// milliseconds later (the selections come from local storage), so this only has to suggest the
// shape of the list, not match its length.
const LOADING_ROWS = 3;

export const ProjectPage = () => {
    const { state: { selectionRepository, persistCallback }, loading } = useDefaultProject();
    const [
        inProgress,
        setInProgress,
    ] = useState<boolean>(false);
    const count = selectionRepository.count();
    const { currentPage, goToPage, goBack, goHome } = useProjectNavigation();
    const { isMobile } = useContext(RuntimeContext);
    const { userSettings, priceAttributeId, selectionsDefaults } = useContext(ConfigContext);
    const translate = useBelimoTranslate();
    const apollo = useApolloClient();
    const offset = (currentPage - 1) * limit;
    const selections = selectionRepository.list(limit, offset);

    const [openedSelection, setOpenedSelection] = useState<string | null>(null);
    const [selectedNames, setSelectedNames] = useState<Set<string>>(new Set());

    const isAllSelected = count > 0 && selectedNames.size === count;
    const isSomeSelected = selectedNames.size > 0 && !isAllSelected;

    // Cross-page drag: the top/bottom drop zones (see CrossPageDropZone) let the user throw a
    // selection onto the previous/next page. All the dnd wiring lives in the hook.
    const {
        hasPrevPage,
        hasNextPage,
        toolbarRef,
        actionsRef,
        rowsRef,
        dndKey,
        dndProviderProps,
    } = useCrossPageDrag({
        selectionRepository,
        persistCallback,
        currentPage,
        goToPage,
        offset,
        limit,
        count,
    });

    const removeHandler = useCallback((selection: Selection) => {
        persistCallback(selectionRepository.remove(selection))
            .then(() => {
                if (selectionRepository.list(limit, offset).length === 0) {
                    const prevPage = currentPage - 1;
                    goToPage(prevPage === 0 ? 1 : prevPage);
                }
            })
        // @TODO handle errors
        // eslint-disable-next-line no-console
            .catch((err) => console.log(err));
    }, [currentPage, offset, goToPage, persistCallback, selectionRepository]);

    const duplicateHandler = useCallback((selection: Selection) => {
        persistCallback(selectionRepository.duplicate(selection))
        // @TODO handle errors
        // eslint-disable-next-line no-console
            .catch((err) => console.log(err));
    }, [persistCallback, selectionRepository]);

    const renameHandler = useCallback((selection: Selection, name: string) => {
        persistCallback(selectionRepository.rename(selection, name))
        // @TODO handle errors
        // eslint-disable-next-line no-console
            .catch((err) => console.log(err));
    }, [persistCallback, selectionRepository]);

    const updateSelectionHandler = useCallback((selection: Selection) => {
        selectionRepository.update(selection);
        persistCallback(selectionRepository.list(selectionRepository.count(), 0))
        // @TODO handle errors
        // eslint-disable-next-line no-console
            .catch((err) => console.log(err));
    }, [persistCallback, selectionRepository]);

    const isNameValid = useCallback(
        (selection: Selection, name: string) => selection.name === name || selectionRepository.get(name) === null,
        [selectionRepository],
    );

    const handleToggleSelect = useCallback((name: string) => {
        setSelectedNames((prev) => {
            const next = new Set(prev);
            if (next.has(name)) {
                next.delete(name);
            } else {
                next.add(name);
            }
            return next;
        });
    }, []);

    const handleSelectAllToggle = useCallback(() => {
        if (isAllSelected) {
            setSelectedNames(new Set());
        } else {
            setSelectedNames(new Set(selectionRepository.list(count, 0).map((s) => s.name)));
        }
    }, [isAllSelected, selectionRepository, count]);

    const handleDeleteSelections = useCallback(() => {
        if (selectedNames.size === 0) {
            return;
        }
        const remaining = selectionRepository
            .list(selectionRepository.count(), 0)
            .filter((s) => !selectedNames.has(s.name));
        confirm({
            header: MESSAGES.projectPageConfirmationModalHeader,
            message: MESSAGES.projectPageRemoveConfirmation,
        }).then((isConfirmed) => (isConfirmed ? persistCallback(remaining).then(() => {
            setSelectedNames(new Set());
            if (remaining.slice(offset, offset + limit).length === 0) {
                const prevPage = currentPage - 1;
                goToPage(prevPage === 0 ? 1 : prevPage);
            }
        }) : Promise.resolve()))
            // eslint-disable-next-line no-console
            .catch((err) => console.log(err));
    }, [selectedNames, selectionRepository, persistCallback, offset, currentPage, goToPage]);

    const selectedRepository = useMemo(() => {
        if (selectedNames.size === 0) {
            return null;
        }
        const items = selectionRepository.list(count, 0).filter((s) => selectedNames.has(s.name));
        return new SelectionRepository(items as Selection[]);
    }, [selectedNames, selectionRepository, count]);

    const addToCartHandler = useCallback(async (selection: Selection) => {
        const messages = await Promise.all(prepareForCart({
            selections: [selection],
            userSettings,
            translate,
        }).map(async ({ content, label, qty }) => {
            if (content === undefined) {
                return Promise.resolve({ label, qty });
            }
            return fetchDesignData({
                content,
                apollo,
            }).then(({ result: designData }) => ({
                label,
                qty,
                designData,
            }));
        }));
        addToCart(messages);
    }, [apollo, translate, userSettings]);

    const checkoutHandler = useCallback(async () => {
        if (inProgress) {
            return;
        }
        try {
            setInProgress(true);
            const repo = selectedRepository ?? selectionRepository;
            const messages = await Promise.all(prepareForCart({
                selections: repo.list(repo.count(), 0),
                userSettings,
                translate,
            }).map(({ content, label, qty }) => {
                if (content === undefined) {
                    return Promise.resolve({ label, qty });
                }
                return fetchDesignData({
                    content,
                    apollo,
                }).then(({ result: designData }) => ({
                    label,
                    qty,
                    designData,
                }));
            }));
            addToCart(messages);
        } finally {
            setInProgress(false);
        }
    }, [apollo, inProgress, selectedRepository, selectionRepository, translate, userSettings]);

    if (inProgress) {
        return ('Adding to cart, please wait');
    }

    if (loading) {
        return (
            <div className="vss-project-page">
                <div className="vss-project-page__contents">
                    <div className="vss-project-page__header">
                        <div className="vss-project-page__title">
                            <I18n messageId={MESSAGES.projectPageTitle}/>
                        </div>
                    </div>

                    <div className="vss-project-page__rows">
                        {Array.from({ length: LOADING_ROWS }, (_, index) => (
                            <SkeletonSelectionRow
                                key={index}
                                withPrice={userSettings.shoppingCenter !== null}
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    const header = (
        <div className="vss-project-page__header">
            <div className="vss-project-page__title">
                <I18n messageId={MESSAGES.projectPageTitle}/>
            </div>

            <Button
                kind={selectionsDefaults.closeButtonType}
                size={selectionsDefaults.closeButtonSize}
                onClick={goBack}
                icon={(
                    <div className="vss-project-page__icon">
                        <CrossIcon/>
                    </div>
                )}
            >
                <I18n messageId={MESSAGES.projectPageGoBack}/>
            </Button>
        </div>
    );

    if (count === 0) {
        return (
            <div className="vss-project-page">
                <div className="vss-project-page__contents">
                    {header}
                    <ProjectEmptySelection/>
                </div>
            </div>
        );
    }

    const selectedSize = (
        <>
            {selectedNames.size}
            {' '}
            <I18n messageId={MESSAGES.projectPageSelectionsCount}/>
        </>
    );

    return (
        <div className="vss-project-page">
            <div className="vss-project-page__contents">
                {header}

                <div className="vss-project-page__selections">
                    <div className="vss-project-page__toolbar" ref={toolbarRef}>
                        <div className="vss-project-page__toolbar-main">
                            <div className="vss-checkbox">
                                <input
                                    type="checkbox"
                                    className="vss-checkbox__input"
                                    id="vss-select-all-checkbox"
                                    checked={isAllSelected}
                                    onChange={handleSelectAllToggle}
                                />
                                <label htmlFor="vss-select-all-checkbox" className="vss-checkbox__label">
                                    <span className={clsx('vss-checkbox__appearance', {
                                        'vss-checkbox__appearance--checked': isAllSelected || isSomeSelected,
                                    })}
                                    >
                                        {isAllSelected && <CheckIcon/>}
                                        {isSomeSelected && <MinusIcon/>}
                                    </span>
                                    <span className={clsx('vss-checkbox__label-text', {
                                        'vss-checkbox__label-text--checked': isAllSelected || isSomeSelected,
                                    })}
                                    >
                                        <I18n messageId={MESSAGES.projectPageSelectAll}/>
                                    </span>
                                </label>
                            </div>

                            <div className="vss-project-page__toolbar-actions">
                                <ExportExcelButton
                                    selectionRepository={selectedRepository ?? selectionRepository}
                                    userSettings={userSettings}
                                    disabled={selectedNames.size === 0}
                                    kind={selectionsDefaults.actionButtonType}
                                    size={selectionsDefaults.actionButtonSize}
                                />

                                {selectionsDefaults.addToCartButtonEnabled && (
                                    <Button
                                        type="button"
                                        kind={selectionsDefaults.actionButtonType}
                                        size={selectionsDefaults.actionButtonSize}
                                        onClick={checkoutHandler}
                                        disabled={selectedNames.size === 0}
                                        icon={(
                                            <div className="vss-project-page__icon">
                                                <CartIcon/>
                                            </div>
                                        )}
                                    >
                                        <span className="vss-project-page__btn-label">
                                            <I18n messageId={MESSAGES.projectPageAddToCartSelection}/>
                                        </span>
                                    </Button>
                                )}

                                <Button
                                    type="button"
                                    kind={selectionsDefaults.actionButtonType}
                                    size={selectionsDefaults.actionButtonSize}
                                    onClick={handleDeleteSelections}
                                    disabled={selectedNames.size === 0}
                                    icon={(
                                        <div className="vss-project-page__icon">
                                            <DeleteIcon/>
                                        </div>
                                    )}
                                >
                                    <span className="vss-project-page__btn-label">
                                        <I18n messageId={MESSAGES.projectPageDeleteSelections}/>
                                    </span>
                                </Button>
                            </div>
                        </div>

                        {selectedNames.size > 0 && (
                            <div className="vss-project-page__summary">
                                {priceAttributeId !== null ? (
                                    <CountTotalPrice
                                        region={userSettings.currentRegion}
                                        locale={userSettings.currentLocale}
                                        priceId={priceAttributeId}
                                        selectionRepository={selectedRepository ?? selectionRepository}
                                    >
                                        {selectedSize}
                                    </CountTotalPrice>
                                ) : (
                                    <div className="vss-project-page__summary-info">
                                        {selectedSize}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <DragDropProvider key={dndKey} {...dndProviderProps}>
                        <CrossPageDropZone
                            direction="prev"
                            messageId={MESSAGES.projectPageDragToPrevPage}
                            disabled={!hasPrevPage}
                        />

                        <div className="vss-project-page__rows" ref={rowsRef}>
                            {selections
                                .map((selection, index) => (
                                    <SortableSelectionRow
                                        key={selection.name}
                                        id={selection.name}
                                        index={index}
                                    >
                                        <ProjectSelectionItem
                                            selection={selection}
                                            updateSelection={updateSelectionHandler}
                                            addToCartHandler={addToCartHandler}
                                            renameHandler={renameHandler}
                                            removeHandler={removeHandler}
                                            duplicateHandler={duplicateHandler}
                                            openedSelection={openedSelection}
                                            setOpenedSelection={setOpenedSelection}
                                            isNameValid={isNameValid}
                                            isSelected={selectedNames.has(selection.name)}
                                            onToggleSelect={handleToggleSelect}
                                        />
                                    </SortableSelectionRow>
                                ))}
                        </div>

                        <CrossPageDropZone
                            direction="next"
                            messageId={MESSAGES.projectPageDragToNextPage}
                            disabled={!hasNextPage}
                        />
                    </DragDropProvider>

                    <Pagination
                        total={count}
                        isMobile={isMobile}
                        currentPage={currentPage}
                        pageSize={limit}
                        onPageChange={goToPage}
                    />

                    <div className="vss-project-page__actions" ref={actionsRef}>
                        <Button
                            kind="primary"
                            onClick={goHome}
                            icon={(
                                <div className="vss-project-page__icon">
                                    <AddProjectSelectionIcon/>
                                </div>
                            )}
                        >
                            <I18n messageId={MESSAGES.projectPageHomePage}/>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
